import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldAlert, Cpu, Layers, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../../components/public/Footer';

export default function DigitalAssets() {
  const [lang, setLang] = useState('En');

  useEffect(() => {
    const syncLang = () => {
      setLang((localStorage.getItem('januslang') || localStorage.getItem('janus_lang')) === 'Fr' ? 'Fr' : 'En');
    };

    syncLang();
    window.addEventListener('langchange', syncLang);
    window.addEventListener('januslangchanged', syncLang);

    return () => {
      window.removeEventListener('langchange', syncLang);
      window.removeEventListener('januslangchanged', syncLang);
    };
  }, []);

  const translations = {
    Fr: {
      metaTitle: "Digital Assets | Janus Capital Strategy",
      metaDesc: "Janus Capital Strategy propose une lecture patrimoniale des digital assets, centrée sur Bitcoin, la diversification, la mobilité du capital et l'intégration disciplinée dans une structure patrimoniale globale.",
      title: "Digital assets : une lecture patrimoniale, sélective et disciplinée.",
      sub: "Nous abordons les actifs digitaux non comme une narration spéculative, mais comme une composante potentielle de diversification, de réserve de valeur et de mobilité du capital.",
      p1: "Chez Janus Capital Strategy, les digital assets sont traités avec retenue, méthode et exigence. Nous ne défendons pas une logique de spéculation généralisée. Nous privilégions une lecture patrimoniale centrée sur les actifs qui peuvent jouer une fonction identifiable dans une structure globale. Dans cette approche, Bitcoin occupe une place particulière : non pas comme promesse, mais comme actif de diversification, de liquidité internationale et de réserve de valeur potentielle dans certains profils de portefeuille.",
      p2: "Nous faisons une distinction claire entre les actifs digitaux sélectionnés pour leur fonction patrimoniale et l'écosystème spéculatif qui détourne trop souvent le sujet de ses usages les plus sérieux. Nous analysons la fonction de l'actif dans le patrimoine, sa liquidité, sa lisibilité, son exposition au risque et son rôle potentiel dans une stratégie internationale, en pleine conformité avec les exigences réglementaires (VARA à Dubaï, MiCA en Europe).",
      gridTitle: "Ce que nous analysons et ce que nous évitons",
      cards: [
        {
          title: "Notre position",
          text: "Nous faisons une distinction claire entre les actifs digitaux sélectionnés pour leur fonction patrimoniale et l'écosystème spéculatif. Cette distinction est essentielle pour conserver une image de conseil cohérente et une allocation disciplinée.",
          icon: <Cpu size={24} />
        },
        {
          title: "Ce que nous évitons",
          text: "Nous évitons les approches fondées sur le bruit de marché, les promesses de gains rapides, les narratifs instables et les actifs sans place claire dans une logique patrimoniale.",
          icon: <Layers size={24} />
        },
        {
          title: "Compliance et Audit Concret",
          text: "Un audit de conformité pour les professionnels (VASP, exchanges, structures crypto), une due diligence sur les actifs, et un accompagnement à la déclaration fiscale et réglementaire dans les cadres VARA et MiCA.",
          icon: <ShieldAlert size={24} />
        }
      ],
      cta: "Découvrir notre approche des digital assets",
      ctaSecondary: "Voir le Portefeuille"
    },
    En: {
      metaTitle: "Digital Assets | Janus Capital Strategy",
      metaDesc: "Janus Capital Strategy offers a wealth-based reading of digital assets, centered on Bitcoin, diversification, capital mobility, and disciplined integration within a global wealth structure.",
      title: "Digital assets: a selective, disciplined wealth reading.",
      sub: "We approach digital assets not as a speculative narrative, but as a potential component of diversification, store of value, and capital mobility.",
      p1: "At Janus Capital Strategy, digital assets are treated with restraint, method and rigor. We do not advocate generalized speculation. We favor a wealth-based reading centered on assets that can play an identifiable function within an overall structure. In this approach, Bitcoin holds a particular place: not as a promise, but as an asset of diversification, international liquidity, and potential store of value within certain portfolio profiles.",
      p2: "We draw a clear distinction between digital assets selected for their wealth function and the speculative ecosystem that too often distracts from their most serious uses. We analyze the asset's function within the wealth structure, its liquidity, its legibility, its risk exposure, and its potential role in an international strategy, fully aligned with regulatory requirements (VARA in Dubai, MiCA in Europe).",
      gridTitle: "What we analyze and what we avoid",
      cards: [
        {
          title: "Our position",
          text: "We draw a clear distinction between digital assets selected for their wealth function and the speculative ecosystem. This distinction is essential to maintain coherent advisory positioning and disciplined allocation.",
          icon: <Cpu size={24} />
        },
        {
          title: "What we avoid",
          text: "We avoid approaches based on market noise, promises of quick gains, unstable narratives, and assets with no clear place in a wealth logic.",
          icon: <Layers size={24} />
        },
        {
          title: "Compliance & Concrete Audit",
          text: "A compliance audit for professionals (VASP, exchanges, crypto structures), asset due diligence, and support with regulatory and tax declarations under VARA and MiCA frameworks.",
          icon: <ShieldAlert size={24} />
        }
      ],
      cta: "Discover our approach to digital assets",
      ctaSecondary: "View Portfolio"
    },
  };

  const t = translations[lang] || translations.En;
  const isRtl = false;

  return (
    <div className="janus-page font-sans selection:bg-[var(--color-gold)] selection:text-[var(--color-surface)]" dir={isRtl ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.metaDesc} />
        <meta property="og:url" content="https://januscapitalstrategy.com/digital-assets" />
        <meta property="og:image" content="https://januscapitalstrategy.com/uploads/images/Image_DIGITAL_ASSETS.webp" />
        <meta property="og:site_name" content="Janus Capital" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.metaTitle} />
        <meta name="twitter:description" content={t.metaDesc} />
        <meta name="twitter:image" content="https://januscapitalstrategy.com/uploads/images/Image_DIGITAL_ASSETS.webp" />
      </Helmet>

      <section className="relative pt-48 pb-32 px-6 lg:px-12 max-w-[1400px] mx-auto text-center border-b border-[var(--color-line)] flex flex-col items-center">
        <h1 className="text-4xl md:text-6xl font-light tracking-wide mb-6 janus-title uppercase">{t.title}</h1>
        <p className="text-[var(--color-gold)] tracking-[0.2em] uppercase text-sm md:text-base font-bold mb-4 text-center max-w-4xl leading-relaxed">{t.sub}</p>
        <img src="https://januscapitalstrategy.com/uploads/images/1784356715968-logo-unifie.webp" alt="Janus Arche" className="h-16 md:h-20 w-auto mt-0 object-contain" />
      </section>

      <section className="py-32 px-6 lg:px-12 max-w-[1200px] mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <p className="janus-text-muted font-light leading-relaxed text-lg text-justify">{t.p1}</p>
            <p className="janus-text-muted font-light leading-relaxed text-lg text-justify">{t.p2}</p>
          </div>
          <div className="w-full aspect-[4/3] sm:aspect-[16/10] relative rounded-sm overflow-hidden shadow-xl border border-[var(--color-line)] janus-surface">
            <img
              src="https://januscapitalstrategy.com/uploads/images/Image_DIGITAL_ASSETS.webp"
              alt="Premium Cryptographic Obsidian Network Architecture"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-32 bg-transparent border-y border-[var(--color-line)] px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-light text-center text-[var(--color-gold)] uppercase tracking-widest mb-16">{t.gridTitle}</h2>
          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {t.cards.map((card, idx) => (
              <div
                key={idx}
                className="p-8 border border-[var(--color-line)] janus-surface hover:border-[var(--color-gold)] hover:bg-[var(--color-gold)] transition-all duration-500 group flex flex-col shadow-sm hover:shadow-xl rounded-sm"
              >
                <div className="text-[var(--color-line)] group-hover:text-white/20 transition-colors duration-500 mb-6">
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold tracking-wide uppercase mb-4 janus-title group-hover:text-white transition-colors duration-500">
                  {card.title}
                </h3>
                <p className="janus-text-muted text-sm font-light leading-relaxed group-hover:text-white/90 transition-colors duration-500 flex-grow">
                  {card.text}
                </p>
                {idx === 2 && (
                  <button 
                    onClick={() => window.open('https://januscapitalstrategy.com/telegram/', '_blank')}
                    className="mt-6 text-[10px] font-bold tracking-widest uppercase border border-current p-2 rounded-sm opacity-60 hover:opacity-100 transition flex items-center justify-center gap-2 group-hover:text-white"
                  >
                    {lang === 'Fr' ? 'Rejoindre' : 'Join Community'} <ExternalLink size={12}/>
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('openContactModal', { detail: { source: 'digital' } }));
                }}
                className="janus-cta-secondary !border-2 !border-[var(--color-black)] hover:!bg-[var(--color-black)] hover:!text-white shadow-xl !px-12 !py-5"
              >
                {t.cta}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
